import { Box, ButtonBase, InputBase, styled } from "@mui/material";

import {
    CATALOG_CARD_MIN_HEIGHT,
    COLORS,
    RADIUS,
    SHADOW,
    TYPOGRAPHY,
} from "../../CreationModal.constants";
import { buttonBaseReset, transientOptions } from "../../CreationModal.styled";

export const Controls = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: 12,
});

export const SearchInput = styled(InputBase)({
    width: "100%",
    padding: "0 14px",
    gap: 8,
    backgroundColor: "rgba(248, 250, 252, 0.6)",
    color: COLORS.slate900,
    borderRadius: RADIUS.lg,
    border: `1px solid ${COLORS.slate200}`,
    boxShadow: SHADOW.xs,
    transition: "border-color 150ms, box-shadow 150ms, background-color 150ms",
    ...TYPOGRAPHY.bodyMd,
    "& svg": { fontSize: 18, color: COLORS.slate400 },
    "& input": {
        padding: "10px 0",
        height: "auto",
        "&::placeholder": { color: COLORS.slate400, opacity: 1 },
    },
    "&.Mui-focused": {
        backgroundColor: COLORS.white,
        borderColor: COLORS.primary,
        boxShadow: `0 0 0 1px ${COLORS.primary}`,
    },
});

/** Row holding the left arrow, the scrollable pills track and the right arrow. */
export const PillsScroller = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
});

export const Pills = styled(Box)({
    display: "flex",
    alignItems: "center",
    flex: "1 1 auto",
    minWidth: 0,
    gap: 8,
    padding: "2px 0",
    overflowX: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
});

export const PillsArrow = styled(
    ButtonBase,
    transientOptions,
)<{ $hidden?: boolean }>(({ $hidden }) => ({
    ...buttonBaseReset,
    display: $hidden ? "none" : "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    width: 28,
    height: 28,
    borderRadius: RADIUS.full,
    backgroundColor: COLORS.slate100,
    color: COLORS.slate600,
    border: `1px solid ${COLORS.slate200}`,
    transition: "background-color 150ms, color 150ms, opacity 150ms",
    "& svg": { fontSize: 18 },
    "&:hover": { backgroundColor: "rgba(226, 232, 240, 0.7)" },
    "&.Mui-disabled": {
        opacity: 0.35,
        cursor: "default",
    },
    "&.Mui-focusVisible": {
        boxShadow: SHADOW.focusRing,
    },
}));

export const Grid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 16,
    [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
}));

export const Card = styled(
    ButtonBase,
    transientOptions,
)<{ $selected?: boolean; $disabled?: boolean }>(({ $selected, $disabled }) => ({
    ...buttonBaseReset,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "space-between",
    textAlign: "left",
    width: "100%",
    minHeight: CATALOG_CARD_MIN_HEIGHT,
    padding: 16,
    borderRadius: RADIUS.lg,
    backgroundColor: $selected ? COLORS.primarySoft : COLORS.white,
    border: $selected
        ? `2px solid ${COLORS.primary}`
        : `1px solid rgba(226, 232, 240, 0.9)`,
    // Keep the 2px selected border from shifting the layout.
    margin: $selected ? 0 : 1,
    boxShadow: $selected ? `${SHADOW.sm}, ${SHADOW.selectedRing}` : "none",
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.55 : 1,
    transition:
        "border-color 150ms, box-shadow 150ms, background-color 150ms, opacity 150ms",
    "&:hover": $disabled
        ? undefined
        : {
              borderColor: $selected ? COLORS.primary : COLORS.slate300,
              boxShadow: $selected
                  ? `${SHADOW.sm}, ${SHADOW.selectedRing}`
                  : SHADOW.sm,
          },
    "&.Mui-focusVisible": {
        borderColor: COLORS.primary,
        boxShadow: `${SHADOW.sm}, ${SHADOW.focusRing}`,
    },
    "& .card-head": {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
    },
    "& .card-title": {
        margin: "12px 0 0",
        color: COLORS.slate900,
        ...TYPOGRAPHY.sectionTitle,
    },
    "& .card-description": {
        margin: "4px 0 0",
        color: COLORS.slate500,
        ...TYPOGRAPHY.bodySm,
        lineHeight: "18px",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    "& .card-foot": {
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 12,
    },
}));

export const Radio = styled(
    "span",
    transientOptions,
)<{ $checked?: boolean }>(({ $checked }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: RADIUS.full,
    transition: "background-color 150ms, border-color 150ms",
    ...($checked
        ? {
              backgroundColor: COLORS.primary,
              border: `2px solid ${COLORS.primary}`,
              color: COLORS.white,
              boxShadow: SHADOW.xs,
          }
        : {
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.slate300}`,
          }),
    "& svg": { fontSize: 13 },
}));

export const Tag = styled("span")({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: RADIUS.sm,
    backgroundColor: COLORS.slate100,
    color: COLORS.slate600,
    ...TYPOGRAPHY.labelXs,
});

export const UnavailableTag = styled(Tag)({
    backgroundColor: "#fef2f2",
    color: COLORS.error,
});

export const EmptyState = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: "48px 16px",
    textAlign: "center",
    color: COLORS.slate500,
    ...TYPOGRAPHY.bodyMd,
    "& strong": {
        color: COLORS.slate900,
        ...TYPOGRAPHY.sectionTitle,
    },
});

export const LoaderWrap = styled(Box)({
    display: "flex",
    justifyContent: "center",
    padding: "48px 0",
});
